// @ts-strict-ignore
import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { RadioButtonModule } from "primeng/radiobutton";
import { TableModule } from "primeng/table";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import { SharedDirectivesModule } from "src/app/shared/shared-directives.module";
import { ErrorMessageComponent } from "src/app/modules/esim/shared/components/error-message/error-message.component";
import * as C from "../../../../../../shared/constants/globals.constants";
import * as MSL from "src/app/shared/constants/msl.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { DataService } from "src/app/shared/services/data.service";
import * as ProfileModel from "src/app/shared/models/profile.model";

@Component({
  selector: C.EWIZ_RAM_INSTANCE_CONFIGURATION,
  templateUrl: "./ram-instance-configuration.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    RadioButtonModule,
    TableModule,
    SharedDirectivesModule,
    ErrorMessageComponent
  ]
})
export class EWizRAMInstanceConfigurationComponent extends EWizDynamicBaseClassComponent implements OnInit, ErrorCheck {
  constructor(private dataService: DataService) {
    super();
  }

  nextTarId = 0;

  ngOnInit() {
    this.errorCheck(this.profileValues, this.dataService);

    for (const tar of this.profileValues.ramConfiguration.tarList.tars) {
      if (tar.tarId > this.nextTarId) {
        this.nextTarId = tar.tarId;
      }
    }
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    const tarValues = [];

    // Push all TAR values from RFM configuration to ensure TAR values entered here are distinct
    for (const configuration of values.rfmConfiguration) {
      if (configuration.tarList.tars.length > 0) {
        for (const tar of configuration.tarList.tars) {
          if (!dataService.isNullOrEmpty(tar.tarValue) && dataService.validHexValue(tar.tarValue, 3, 3)) {
            if (!tarValues.includes(tar.tarValue.toLowerCase())) {
              tarValues.push(tar.tarValue.toLowerCase());
            }
          }
        }
      }
    }

    if (values.ramConfiguration.tarList.tars.length > 0) {
      // All TAR values must be entered and valid
      for (const tar of values.ramConfiguration.tarList.tars) {
        if (dataService.isNullOrEmpty(tar.tarValue)) {
          this.errorMessage = $localize`TAR values must be entered`;
          this.errorFound = true;
          break;
        } else if (!dataService.validHexValue(tar.tarValue, 3, 3)) {
          this.errorMessage = $localize`TAR values must be 3 HEX bytes in length`;
          this.errorFound = true;
          break;
        } else {
          if (tarValues.includes(tar.tarValue.toLowerCase())) {
            this.errorMessage = $localize`TAR values must be unique across RFM and RAM configuration`;
            this.errorFound = true;
            break;
          } else {
            tarValues.push(tar.tarValue.toLowerCase());
          }
        }
      }
    }

    if (values.ramConfiguration.mslValue !== null) {
      // Each RAM instance must have a MSL value entered and valid
      if (dataService.isNullOrEmpty(values.ramConfiguration.mslValue)) {
        this.errorMessage = $localize`MSL value must be entered`;
        this.errorFound = true;
      } else if (!dataService.validHexValue(values.ramConfiguration.mslValue, 1, 1)) {
        this.errorMessage = $localize`MSL value must be 1 HEX byte in length`;
        this.errorFound = true;
      } else {
        let mslValueValid = false;

        for (const value of MSL.MSL_VALUE_MATRIX) {
          if (value[MSL.MSL_HEX_CODE].toUpperCase() === values.ramConfiguration.mslValue.toUpperCase()) {
            mslValueValid = true;
            break;
          }
        }

        if (!mslValueValid) {
          if (parseInt(values.ramConfiguration.mslValue, 16) >= 32) {
            this.errorMessage = $localize`MSL value is invalid, values of 20 and above are RFU`;
            this.errorFound = true;
          } else {
            this.errorMessage = $localize`MSL value is invalid`;
            this.errorFound = true;
          }
        }
      }
    }

    return this.errorFound;
  }

  addTar() {
    this.profileValues.ramConfiguration.tarList.tars.push({
      tarId: ++this.nextTarId,
      tarValue: null
    });

    this.handleFormEvents();
  }

  deleteTar() {
    const newTarList = [];

    for (const originalTar of this.profileValues.ramConfiguration.tarList.tars) {
      let removeTar = false;

      for (const selectedTar of this.profileValues.ramConfiguration.tarList.selectedTars) {
        if (originalTar.tarId === selectedTar.tarId) {
          removeTar = true;
          break;
        }
      }

      if (!removeTar) {
        newTarList.push(originalTar);
      }
    }

    this.profileValues.ramConfiguration.tarList.tars = [...newTarList];
    this.profileValues.ramConfiguration.tarList.selectedTars = [];

    this.handleFormEvents();
  }

  mslInputEventHandler() {
    this.profileValues.ramConfiguration.integrityCheckOption = null;
    this.profileValues.ramConfiguration.cipheringOption = null;
    this.profileValues.ramConfiguration.counterOption = null;

    if (
      !this.dataService.isNullOrEmpty(this.profileValues.ramConfiguration.mslValue) &&
      this.dataService.validHexValue(this.profileValues.ramConfiguration.mslValue, 1, 1) &&
      this.dataService.hexValueInRange(this.profileValues.ramConfiguration.mslValue, 0x00, 0xff)
    ) {
      for (const value of MSL.MSL_VALUE_MATRIX) {
        if (value[MSL.MSL_HEX_CODE].toLowerCase() === this.profileValues.ramConfiguration.mslValue.toLowerCase()) {
          this.profileValues.ramConfiguration.integrityCheckOption = value[MSL.MSL_INTEGRITY_CHECK];
          this.profileValues.ramConfiguration.cipheringOption = value[MSL.MSL_CIPHERING];
          this.profileValues.ramConfiguration.counterOption = value[MSL.MSL_COUNTER];
          break;
        }
      }
    }

    this.handleFormEvents();
  }

  mslRadioEventHandler() {
    this.profileValues.ramConfiguration.mslValue = null;

    for (const value of MSL.MSL_VALUE_MATRIX) {
      if (
        +this.profileValues.ramConfiguration.integrityCheckOption === +value[MSL.MSL_INTEGRITY_CHECK] &&
        +this.profileValues.ramConfiguration.cipheringOption === +value[MSL.MSL_CIPHERING] &&
        +this.profileValues.ramConfiguration.counterOption === +value[MSL.MSL_COUNTER]
      ) {
        this.profileValues.ramConfiguration.mslValue = `${value[MSL.MSL_HEX_CODE]}`;
        break;
      }
    }

    this.handleFormEvents();
  }
}
